<template>
    <div class="livestream-player-wrap" :style="cssProps">

        <template v-if="currentStream === null && noLivestreamMsg != ''">
            <p class="no-livestreams" v-html="noLivestreamMsg"></p>
        </template>

        <!-- The countdown keeps track of when a stream is live or not, so it must always be present -->
        <sc-wp-countdown-widget @stream-ended="handleStreamEnd" @stream-started="handleStreamStart"
            @stream-updated="updateStream" :dark="dark" :currentStream="currentStream" :hasCountdown="hasCountdown"
            :settings="countdownOptions" :eventCategory="eventCategory" :version="version" :class="{
                'invisible': isHideCountdown
            }">
        </sc-wp-countdown-widget>

        <template v-if="currentStream && currentStream.m3u8">
            <div class="livestream-now">
                <p class="live-now-text" :style="liveTextCssProps" v-html="liveNowText"></p>
                <a class="watch-now-link"
                    :style="buttonCssProps"
                    :href="currentWatchUrl"
                    target="_blank">
                    {{ watchText || 'Watch Now' }}
                </a>
            </div>
        </template>
    </div>
</template>

<script>
import { EventBus } from '../../mixins/EventBus';

export default {
    props: {
        streams: Object,
        streamSettings: Object,
        version: String
    },
    data() {
        return {
            currentStream: undefined,
            dark: true,
            hasCountdown: true,
            isStreaming: false,
            player: undefined,
            isStreamPreparing: false,
            isStreamReady: false,
            isHideCountdown: false,
            eventCategory: ''
        }
    },
    created() {
        // Parse url search query looking for embed specific styling
        var queryObject = this.getSearchQueryAsObject();
        this.eventCategory = queryObject.event_category || '';

        // The countdown is shown always
        this.hasCountdown = true;

        this.noLivestreamMsg = queryObject.noLivestreamMsg ? decodeURIComponent(queryObject.noLivestreamMsg.replaceAll("+", " ")) : 'There are currently no livestreams scheduled';
        this.watchText = queryObject.watchText ? decodeURIComponent(queryObject.watchText.replaceAll("+", " ")) : 'Watch';
        this.liveNowText = queryObject.liveNowText ? decodeURIComponent(queryObject.liveNowText.replaceAll("+", " ")) : 'Live Now';

        // countdown options
        let countdownTitle = queryObject.title ? decodeURIComponent(queryObject.title.replaceAll("+", " ")) : 'Next Live Stream';
        let countdownTitlePosition = queryObject.titlePosition || 'top';
        let countdownShowDays = !queryObject.showDays || queryObject.showDays === '1';
        let countdownShowHours = !queryObject.showHours || queryObject.showHours === '1';
        let countdownShowMinutes = !queryObject.showMins || queryObject.showMins === '1';
        let countdownShowSeconds = !queryObject.showSecs || queryObject.showSecs === '1';

        //countdown styles
        let countdownContainerWidth = queryObject.countdownContainerWidth ? (queryObject.countdownContainerWidth + "%") : '100%';
        let countdownBackgroundColor = queryObject.countdownBackgroundColor || '';
        let countdownTextColor = queryObject.countdownTextColor || '';
        let countdownTypography = this.getTypographyObjectFromQuery(queryObject, 'countdownTypography');

        let titleTextColor = queryObject.titleTextColor || '';
        let titleTypography = this.getTypographyObjectFromQuery(queryObject, 'titleTypography');

        // for live button
        let liveButtonTypography = this.getTypographyObjectFromQuery(queryObject, 'liveButtonTypography');
        let liveButtonBorder = this.getBorderObjectFromQuery(queryObject, 'liveButtonBorder');
        let liveButtonBoxShadow = this.getBoxShadowObjectFromQuery(queryObject, 'liveButtonBoxShadow');

        let liveButtonTextColorNormalMode = queryObject.liveButtonTextColorNormalMode || '';
        let liveButtonBgColorNormalMode = queryObject.liveButtonBgColorNormalMode || '';
        let liveButtonTextColorHoverMode = queryObject.liveButtonTextColorHoverMode || '';
        let liveButtonBgColorHoverMode = queryObject.liveButtonBgColorHoverMode || '';

        // for live text
        let liveTextTypography = this.getTypographyObjectFromQuery(queryObject, 'liveTextTypography');
        let liveTextBorder = this.getBorderObjectFromQuery(queryObject, 'liveTextBorder');
        let liveTextBoxShadow = this.getBoxShadowObjectFromQuery(queryObject, 'liveTextBoxShadow');

        let liveTextTextColorNormalMode = queryObject.liveTextTextColorNormalMode || '';
        let liveTextBgColorNormalMode = queryObject.liveTextBgColorNormalMode || '';


        this.countdownOptions = {
            title: countdownTitle,
            titlePosition: countdownTitlePosition,
            showDays: countdownShowDays,
            showHours: countdownShowHours,
            showMinutes: countdownShowMinutes,
            showSeconds: countdownShowSeconds,
            // styles
            containerWidth: countdownContainerWidth,
            backgroundColor: countdownBackgroundColor,
            textColor: countdownTextColor,
            typography: countdownTypography,
            titleTextColor: titleTextColor,
            titleTypography: titleTypography,
            // live button styles
            liveButtonTypography: liveButtonTypography,
            liveButtonBorder: liveButtonBorder,
            liveButtonBoxShadow: liveButtonBoxShadow,
            liveButtonTextColorNormalMode: liveButtonTextColorNormalMode,
            liveButtonBgColorNormalMode: liveButtonBgColorNormalMode,
            liveButtonTextColorHoverMode: liveButtonTextColorHoverMode,
            liveButtonBgColorHoverMode: liveButtonBgColorHoverMode,
            // live text styles
            liveTextTypography: liveTextTypography,
            liveTextBorder: liveTextBorder,
            liveTextBoxShadow: liveTextBoxShadow,
            liveTextTextColorNormalMode: liveTextTextColorNormalMode,
            liveTextBgColorNormalMode: liveTextBgColorNormalMode,
        };

        // The countdown uses the dark theme by default and can use the light theme with a query
        this.dark = queryObject.theme === 'light' ? false : true;

        // Set the current stream to the 1st stream listed
        this.currentStream = this.streams ? this.streams.data[0] : null;

        if (this.currentStream && this.currentStream.m3u8) {
            this.currentWatchUrl = "https://" + this.streamSettings.sermon_cloud_slug + "." + this.getRootDomain(location.origin) + "/live";
        } else {
            this.currentWatchUrl = '';
        }


        this.isStreamPreparing = false;
        this.isStreamReady = false;

        // Get the current player from child components
        EventBus.$on('player', (e) => {
            this.player = e;
        });

        // Listen for streaming dashboard hide / show toggles
        this.handleDashboardToggleEvents();
    },
    computed: {

        buttonCssProps() {
            return {
                '--live-button-font-family': this.countdownOptions.liveButtonTypography.font_family || 'inherit',
                '--live-button-font-size': this.countdownOptions.liveButtonTypography.font_size || 'inherit',
                '--live-button-font-weight': this.countdownOptions.liveButtonTypography.font_weight || 'inherit',
                '--live-button-line-height': this.countdownOptions.liveButtonTypography.line_height || 'inherit',
                '--live-button-letter-spacing': this.countdownOptions.liveButtonTypography.letter_spacing || 'inherit',
                '--live-button-border-style': this.countdownOptions.liveButtonBorder.border_style || 'inherit',
                '--live-button-border-width': this.countdownOptions.liveButtonBorder.border_width || 'inherit',
                '--live-button-border-color': this.countdownOptions.liveButtonBorder.border_color || 'inherit',
                '--live-button-box-shadow': this.countdownOptions.liveButtonBoxShadow.box_shadow || 'inherit',
                '--live-button-text-color-normal-mode': this.countdownOptions.liveButtonTextColorNormalMode || 'inherit',
                '--live-button-bg-color-normal-mode': this.countdownOptions.liveButtonBgColorNormalMode || 'inherit',
                '--live-button-text-color-hover-mode': this.countdownOptions.liveButtonTextColorHoverMode || 'inherit',
                '--live-button-bg-color-hover-mode': this.countdownOptions.liveButtonBgColorHoverMode || 'inherit',
            };
        },

        liveTextCssProps() {
            return {
                '--live-text-font-family': this.countdownOptions.liveTextTypography.font_family || 'inherit',
                '--live-text-font-size': this.countdownOptions.liveTextTypography.font_size || 'inherit',
                '--live-text-font-weight': this.countdownOptions.liveTextTypography.font_weight || 'inherit',
                '--live-text-line-height': this.countdownOptions.liveTextTypography.line_height || 'inherit',
                '--live-text-letter-spacing': this.countdownOptions.liveTextTypography.letter_spacing || 'inherit',
                '--live-text-border-style': this.countdownOptions.liveTextBorder.border_style || 'inherit',
                '--live-text-border-width': this.countdownOptions.liveTextBorder.border_width || 'inherit',
                '--live-text-border-color': this.countdownOptions.liveTextBorder.border_color || 'inherit',
                '--live-text-box-shadow': this.countdownOptions.liveTextBoxShadow.box_shadow || 'inherit',
                '--live-text-text-color-normal-mode': this.countdownOptions.liveTextTextColorNormalMode || 'inherit',
                '--live-text-bg-color-normal-mode': this.countdownOptions.liveTextBgColorNormalMode || 'inherit',
            };
        },

        /**
         * Expose user set css variables for use throughout the embed
         */
        cssProps() {
            return {
                '--user-alternate-color': this.streamSettings.alternate_color,
                '--user-header-color': this.streamSettings.header_color,
                '--user-link-color': this.streamSettings.link_color
            };
        },

        /**
         * Reformat the current stream data to match the sermon model so we can use the same playlist item
         */
        playlistData() {
            if (!this.currentStream) {
                return {};
            }

            var playlistData = {
                media: {},
                summary: this.currentStream.description,
                passages: this.currentStream.passages,
                title: this.currentStream.title,
                preacher: this.currentStream.preacher,
                date: this.currentStream.start,
                categories: this.currentStream.eventCategories,
                series: this.currentStream.series,
            };

            // If this is not audio only, add audio & video media items which will show the audio & video selectors
            if (!this.audioOnly) {
                playlistData.media = {
                    audio: this.currentStream.m3u8,
                    video: this.currentStream.m3u8,
                };
            }

            return playlistData;
        },

        /**
         * Check that the stream rotator has content
         *
         * @returns {number}
         */
        useStreamRotator() {
            return this.currentStream &&
                this.currentStream.offline_content.rotator &&
                Object.keys(this.currentStream.offline_content.rotator).length;
        },

        /**
         * Check the the stream specific video loop has content
         *
         * @returns {number}
         */
        useStreamVideoLoop() {
            return this.currentStream &&
                this.currentStream.offline_content.video &&
                Object.keys(this.currentStream.offline_content.video).length;
        },

        /**
         * Check the the stream specific image has content
         *
         * @returns {number}
         */
        useStreamImage() {
            return this.currentStream &&
                this.currentStream.offline_content.image &&
                Object.keys(this.currentStream.offline_content.image).length;
        },

        /**
         * Check the default settings rotator has content
         *
         * @returns {number}
         */
        useSettingsRotator() {
            return this.streamSettings.default_offline_content.rotator &&
                Object.keys(this.streamSettings.default_offline_content.rotator).length;
        },

        /**
         * Check the default settings video loop has content
         *
         * @returns {number}
         */
        useSettingsVideoLoop() {
            return this.streamSettings.default_offline_content.video &&
                Object.keys(this.streamSettings.default_offline_content.video).length;
        }
    },
    methods: {

        getTypographyObjectFromQuery(query, key) {
            let fontFamily = query[key + '[font_family]'];
            return {
                'font_family': fontFamily ? fontFamily.replaceAll('+', ' ') : '',
                'font_size': query[key + '[font_size]'] || '',
                'font_weight': query[key + '[font_weight]'] || '',
                'line_height': query[key + '[line_height]'] || '',
                'letter_spacing': query[key + '[letter_spacing]'] || ''
            };
        },

        getBorderObjectFromQuery(query, key) {
            return {
                'border_style': query[key + '[border_style]'] || '',
                'border_width': query[key + '[border_width]'] || '',
                'border_color': query[key + '[border_color]'] || '',
            };
        },

        getBoxShadowObjectFromQuery(query, key) {
            let boxShadow = query[key + '[box_shadow]'];
            return {
                'box_shadow': boxShadow ? boxShadow.replaceAll('+', ' ') : '',
            };
        },

        getRootDomain(url) {
            // Create a URL object (add scheme if missing)
            if (!/^https?:\/\//i.test(url)) {
                url = "http://" + url;
            }

            const hostname = new URL(url).hostname;

            // Split into parts
            const parts = hostname.split(".");

            // If only 2 parts, return as is (already root)
            if (parts.length <= 2) {
                return hostname;
            }

            // Handle cases like .co.uk (public suffixes)
            const publicSuffixes = ["co.uk", "org.uk", "gov.uk", "ac.uk"];
            const lastTwo = parts.slice(-2).join(".");
            const lastThree = parts.slice(-3).join(".");

            if (publicSuffixes.includes(lastTwo)) {
                return lastThree;
            }

            return lastTwo;
        },


        /**
         * Convert the search query to an object so we can know about user set countdown display & dark mode
         *
         * @returns {Object}
         */
        getSearchQueryAsObject() {
            var queryString = window.location.search.substr(1);
            var obj = {};
            var pairs = queryString.split('&');
            for (var i = 0; i < pairs.length; i++) {
                var pair = pairs[i].split('=');
                obj[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
            }
            return obj;
        },

        /**
         * The countdown tells us when a stream starts, then we hide offline content and show the stream video
         */
        handleStreamStart() {
            this.isStreaming = true;

            window.parent.postMessage(['streamLive'], '*');

            if (typeof window.sermonCloudResizeHeight == 'function') {
                window.sermonCloudResizeHeight();
            }
        },

        /**
         * The countdown tells us when a stream ends, then we hide the old stream, switch to the next stream,
         * and show offline content relevant to the upcoming stream
         *
         * @param {Object} nextStream - the next stream to display
         */
        handleStreamEnd(nextStream) {
            this.isStreaming = false;
            this.currentStream = nextStream;

            window.parent.postMessage(['streamOffline', nextStream], '*');

            if (typeof window.sermonCloudResizeHeight == 'function') {
                window.sermonCloudResizeHeight();
            }
        },

        /**
         * Update the current stream with any changed information from the server
         *
         * @param {Object} stream - a stream with up to date data
         */
        updateStream(stream) {
            this.currentStream = stream;

            window.parent.postMessage(['streamUpdated', stream], '*');

            if (typeof window.sermonCloudResizeHeight == 'function') {
                window.sermonCloudResizeHeight();
            }
        },

        /**
         * Update the video player when the streaming dashboard hides / shows the video
         */
        handleDashboardToggleEvents() {
            let self = this;

            // Streams all start out muted
            let isMutedByUser = true;

            // Streams all start out unpaused
            let isPausedByUser = false;

            window.addEventListener(
                'message',
                (e) => {
                    var eventName = e.data[0];
                    switch (eventName) {
                        case 'hideStream':
                            // Remember if the video was muted or not before hiding
                            if (self.player.muted()) {
                                isMutedByUser = true;
                            } else {
                                isMutedByUser = false;

                                // Mute the video when we hide the stream so sound isn't coming from nowhere
                                self.player.muted(true);
                            }

                            // Remember if the video was paused or not before hiding
                            if (self.player.paused()) {
                                isPausedByUser = true;
                            } else {
                                isPausedByUser = false;
                            }

                            break;
                        case 'showStream':
                            // If the video was unmuted before we hid the dashboard, unmute it when showing
                            if (!isMutedByUser) {
                                self.player.muted(false);
                            }

                            // If the video was playing before we hid the dashboard, start playing it when showing
                            // This is needed for an edge case, where the 1st time a video is unhidden it gets paused
                            if (!isPausedByUser) {
                                self.player.play();
                            }

                            break;
                        case 'showPreparingStream':
                            self.isStreamPreparing = true;
                            window.parent.postMessage(['showPreparingStreamTriggered'], '*');
                            break;
                        case 'hidePreparingStream':
                            self.isStreamPreparing = false;
                            break;
                        case 'showReadyStream':
                            self.isStreamReady = true;
                            self.isStreamPreparing = false;
                            self.isHideCountdown = true;
                            break;
                        case 'hideReadyStream':
                            self.isStreamReady = false;
                            break;
                        case 'hideCountdown':
                            self.isHideCountdown = true;
                            break;
                        case 'showCountdown':
                            self.isHideCountdown = false;
                            break;
                    }
                },
                false
            );
        },

        goLiveClicked() {
            window.parent.postMessage(['goLive'], '*');
        }
    }
}
</script>

<style lang="scss" scoped>
.invisible {
    display: none;
}

.livestream-player-wrap {
    background-color: rgba(35, 35, 35, 1);
    color: rgba(255, 255, 255, 1);
    border-bottom: 1px solid rgba(155, 155, 155, 0.16);
    box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.05);
    position: relative;

    .livestream-preparing {
        z-index: 5000;
        position: absolute;
        width: 100%;
        top: 50%;
        background: rgb(1 1 1 / 60%);
        translate: translateY(-50%);
        transform: translateY(-50%);
        display: flex;
        flex-direction: column;
        padding: 30px 0;

        .livestream-preparing-content {
            display: flex;
            gap: 50px;
            justify-content: center;

            .livestream-spinner,
            .livestream-spinner div,
            .livestream-spinner div:after {
                box-sizing: border-box;
            }

            .livestream-spinner {
                color: currentColor;
                display: inline-block;
                position: relative;
                width: 48px;
                height: 48px;
            }

            .livestream-spinner div {
                transform-origin: 40px 30px;
                animation: livestream-spinner 1.2s linear infinite;
            }

            .livestream-spinner div:after {
                content: " ";
                display: block;
                position: absolute;
                top: 3.2px;
                left: 36.8px;
                width: 5.5px;
                height: 14px;
                border-radius: 4px;
                background: currentColor;
            }

            .livestream-spinner div:nth-child(1) {
                transform: rotate(0deg);
                animation-delay: -0.7s;
            }

            .livestream-spinner div:nth-child(2) {
                transform: rotate(45deg);
                animation-delay: -0.6s;
            }

            .livestream-spinner div:nth-child(3) {
                transform: rotate(90deg);
                animation-delay: -0.5s;
            }

            .livestream-spinner div:nth-child(4) {
                transform: rotate(135deg);
                animation-delay: -0.4s;
            }

            .livestream-spinner div:nth-child(5) {
                transform: rotate(180deg);
                animation-delay: -0.3s;
            }

            .livestream-spinner div:nth-child(6) {
                transform: rotate(225deg);
                animation-delay: -0.2s;
            }

            .livestream-spinner div:nth-child(7) {
                transform: rotate(270deg);
                animation-delay: -0.1s;
            }

            .livestream-spinner div:nth-child(8) {
                transform: rotate(315deg);
                animation-delay: 0s;
            }

            @keyframes livestream-spinner {
                0% {
                    opacity: 1;
                }

                100% {
                    opacity: 0;
                }
            }

            .livestream-preparing-text {
                display: flex;
                flex-direction: column;
                justify-content: center;

                h3 {
                    font-size: 20px;
                    font-weight: 700;
                    line-height: 28px;
                }

                p {
                    line-height: 24px;
                }
            }
        }
    }

    .livestream-ready {
        position: absolute;
        width: 100%;
        top: 50%;
        background: rgb(24 99 204 / 85%);
        translate: translateY(-50%);
        transform: translateY(-50%);
        display: flex;
        flex-direction: column;
        padding: 30px 0;

        .livestream-ready-content {
            display: flex;
            gap: 30px;
            justify-content: center;
            align-items: center;

            .livestream-ready-play-icon {
                font-size: 35px;
                border: 5px solid white;
                border-radius: 50%;
                width: 48px;
                display: flex;
                justify-content: center;
                height: 48px;
                vertical-align: middle;
                line-height: 38px;
                cursor: pointer;
            }

            .livestream-ready-text {
                display: flex;
                flex-direction: column;
                justify-content: center;

                h3 {
                    font-size: 20px;
                    font-weight: 700;
                    line-height: 28px;
                }

                p {
                    line-height: 24px;
                }
            }
        }
    }

    .sermon-player {
        width: 100%;
    }
}

// Style offline content images when there are not rotators or video loops to display
// Ensure they are full width and the same height as the video which will replace them
.livestream-image {
    height: 0;
    overflow: hidden;
    padding-bottom: 56.25%;

    img {
        height: auto;
        width: 100%;
    }
}

.no-livestreams {
    min-height: 500px;
    align-items: center;
    text-align: center;
    width: 100%;
    justify-content: center;
    display: flex;
}

.livestream-now {
    display: flex;
    flex-flow: column;
    gap: 0.5em;
    padding: 1.5em 0;
    align-items: center;
}

.live-now-text {
    display: flex;
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
}

.live-now-text {
    color: var(--live-text-text-color-normal-mode, #FFFFFF);
    background: var(--live-text-bg-color-normal-mode, inherit);
    font-family: var(--live-text-font-family, inherit);
    font-size: var(--live-text-font-size, inherit);
    font-weight: var(--live-text-font-weight, inherit);
    line-height: var(--live-text-line-height, inherit);
    letter-spacing: var(--live-text-letter-spacing, inherit);
    border-style: var(--live-text-border-style, inherit);
    border-width: var(--live-text-border-width, inherit);
    border-color: var(--live-text-border-color, inherit);
    box-shadow: var(--live-text-box-shadow, inherit);
}

a.watch-now-link {
    display: inline-block;
    padding: 8px 16px;
    color: white;
    background: #1863CC;
    font-size: 16px;
    min-width: 120px;
    align-items: center;
    text-align: center;
}

a.watch-now-link:hover {
    text-decoration: none;
}

a.watch-now-link {
    color: var(--live-button-text-color-normal-mode, #FFFFFF);
    background: var(--live-button-bg-color-normal-mode, #1863CC);
    font-family: var(--live-button-font-family, inherit);
    font-size: var(--live-button-font-size, inherit);
    font-weight: var(--live-button-font-weight, inherit);
    line-height: var(--live-button-line-height, inherit);
    letter-spacing: var(--live-button-letter-spacing, inherit);
    border-style: var(--live-button-border-style, inherit);
    border-width: var(--live-button-border-width, inherit);
    border-color: var(--live-button-border-color, inherit);
    box-shadow: var(--live-button-box-shadow, inherit);
}
a.watch-now-link:hover {
    color: var(--live-button-text-color-hover-mode, #FFFFFF);
    background: var(--live-button-bg-color-hover-mode, #1863CC);
}
</style>
